/*
 * These functions are used with sync'd charts.   reset() and highlight() need to be applied
 * to the global Highcharts.prototype object:
 *
 *   Highcharts.Pointer.prototype.reset = reset;
 *   Highcharts.Point.prototype.highlight = highlight;
 *
 * syncExtremes should be used as an event handler for the x-axis:
 *   chart: {
 *     xAxis: {
 *       crosshair: true,
 *       events: {
 *         setExtremes: syncExtremes
 *       },
 *       visible: false
 *     }
 *   }
 *
 * See http://jsfiddle.net/gh/get/jquery/1.9.1/highslide-software/highcharts.com/tree/master/samples/highcharts/demo/synchronized-charts/
 */

import Highcharts from 'highcharts';
import _get from 'lodash/get';

// Store original functions before overriding
let originalReset = null;
let originalHighlight = null;

/**
 * Override the reset function, we don't need to hide the tooltips and crosshairs for synced charts.
 * This is compatible with the accessibility module.
 */
export function reset() {
  // For accessibility keyboard navigation, we still need to allow some reset behavior
  // Only prevent hiding tooltips/crosshairs, but allow other cleanup
  return undefined;
}

/**
 * Highlight a point by showing tooltip, setting hover state and draw crosshair.
 * Works with both mouse events and keyboard navigation.
 */
export function highlight(event) {
  this.onMouseOver(); // Show the hover marker
  this.series.chart.tooltip.refresh(this); // Show the tooltip

  // Only draw crosshair if we have a valid event with coordinates
  if (event && event.chartX !== undefined && this.series.chart.xAxis[0]) {
    this.series.chart.xAxis[0].drawCrosshair(event, this);
  }
}

/**
 * Store original Highcharts functions
 */
export function storeOriginalFunctions(Highcharts) {
  if (!originalReset) {
    originalReset = Highcharts.Pointer.prototype.reset;
  }
  if (!originalHighlight) {
    originalHighlight = Highcharts.Point.prototype.highlight;
  }
}

/**
 * Synchronize zooming through the setExtremes event handler.
 */
export function syncExtremes(e) {
  const thisChart = this.chart;

  if (e.trigger !== 'syncExtremes') {
    // Prevent feedback loop
    Highcharts.each(Highcharts.charts, function (chart) {
      if (chart !== thisChart) {
        if (chart.xAxis[0].setExtremes) {
          // It is null while updating
          chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
            trigger: 'syncExtremes',
          });
        }
      }
    });
  }
}

export function syncHover(charts) {
  return function (e) {
    let chart, point, i, event;

    for (i = 0; i < charts.length; i++) {
      chart = charts[i];
      event = chart.pointer.normalize(e); // Find coordinates within the chart
      point = chart.series[0].searchPoint(event, true); // Get the hovered point

      if (point) {
        point.highlight(e);
      }
    }
  };
}

/*
 * performs a series of configuration updates to the provided highcharts config
 */
export function normalizeChartConfig(chartConfig) {
  // this converts x values to scientific notation, except for the very first value which is left alone
  // because of a (possible) bug in highcharts that displays no data points when all x values are strings
  // chartConfig.series = chartConfig.series.map((series) => {
  //   return {
  //     ...series,
  //     data: series.data.map((row, i) => {
  //       if (i === 0) {
  //         return row;
  //       } else if (
  //         typeof row === 'object' &&
  //         row.length !== undefined &&
  //         row[0] >= 1000000000
  //       ) {
  //         return { x: Number.parseFloat(row[0]).toExponential(1), y: row[1] };
  //       } else if (row.hasOwnProperty('x') && row.x > 1000000000) {
  //         return { ...row, x: Number.parseFloat(row.x).toExponential(1) };
  //       } else {
  //         return row;
  //       }
  //     }),
  //   };
  // });

  // show scientific notation for x-axis labels for logarithmic axes
  if (chartConfig.xAxis && chartConfig.xAxis.type === 'logarithmic') {
    chartConfig.xAxis.labels.formatter = function () {
      if (this.value > 1000000000) {
        return Number.parseFloat(this.value).toExponential(1);
      } else {
        return this.value;
      }
    };
  }

  // formatter functions come through as strings and must be evaluated
  if (
    typeof _get(chartConfig, 'xAxis.labels.formatter', undefined) === 'string'
  ) {
    chartConfig.xAxis.labels.formatter = parseJsonFunction(
      chartConfig.xAxis.labels.formatter
    );
  }

  if (
    typeof _get(chartConfig, 'yAxis.labels.formatter', undefined) === 'string'
  ) {
    chartConfig.yAxis.labels.formatter = parseJsonFunction(
      chartConfig.yAxis.labels.formatter
    );
  }

  if (typeof _get(chartConfig, 'tooltip.formatter', undefined) === 'string') {
    chartConfig.tooltip.formatter = parseJsonFunction(
      chartConfig.tooltip.formatter
    );
  }

  // sync extremes for sync'd charts
  if (chartConfig.xAxis) {
    chartConfig.xAxis.events = { setExtremes: syncExtremes };
  }

  // Add accessibility configuration
  if (!chartConfig.accessibility) {
    chartConfig.accessibility = {};
  }

  // Enable accessibility module
  if (chartConfig.accessibility.enabled !== false) {
    chartConfig.accessibility.enabled = true;
  }

  // Add description for screen readers based on chart title
  if (!chartConfig.accessibility.description && chartConfig.title?.text) {
    chartConfig.accessibility.description = chartConfig.title.text;
  }

  // Ensure axes have titles for better screen reader context
  if (chartConfig.xAxis && !chartConfig.xAxis.accessibility) {
    chartConfig.xAxis.accessibility = {};
  }
  if (chartConfig.yAxis && !chartConfig.yAxis.accessibility) {
    chartConfig.yAxis.accessibility = {};
  }

  // Configure keyboard navigation
  if (!chartConfig.accessibility.keyboardNavigation) {
    chartConfig.accessibility.keyboardNavigation = {
      enabled: true,
    };
  }

  // Ensure tooltips are shown during keyboard navigation by configuring point navigation
  if (!chartConfig.accessibility.point) {
    chartConfig.accessibility.point = {};
  }

  // Make sure tooltips appear when navigating with keyboard
  if (!chartConfig.plotOptions) {
    chartConfig.plotOptions = {};
  }
  if (!chartConfig.plotOptions.series) {
    chartConfig.plotOptions.series = {};
  }
  if (!chartConfig.plotOptions.series.point) {
    chartConfig.plotOptions.series.point = {};
  }
  if (!chartConfig.plotOptions.series.point.events) {
    chartConfig.plotOptions.series.point.events = {};
  }

  // Add event handlers to ensure tooltips show on keyboard focus
  // Store any existing mouseOver handler
  const existingMouseOver = chartConfig.plotOptions.series.point.events.mouseOver;
  chartConfig.plotOptions.series.point.events.mouseOver = function () {
    // Call existing handler if present
    if (existingMouseOver) {
      existingMouseOver.apply(this, arguments);
    }
    // Ensure tooltip shows
    if (this.series && this.series.chart && this.series.chart.tooltip) {
      this.series.chart.tooltip.refresh(this);
    }
  };

  return chartConfig;
}

function parseJsonFunction(stringFunc) {
  return Function('"use strict";return (' + stringFunc + ')')();
}
